/**
 * Email Options Component
 * Shows in the module settings modal to enable/disable accounts for email
 * For calendar modules, also shows expandable calendar picker per account
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSession } from '@/features/auth/hooks/useSession';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { queryKeys } from '@/lib/query/keys';
import { sortGoogleCalendars } from './calendar-sort';
import { emailApiPath } from '@/lib/email-api';
import { useHideDemo } from '@/features/demo/demoVisibility';
import { isDemoIntegrationId } from './demoAccounts';
import { getProviderDisplay, getConnectLabel, isV2, EMAIL_PROVIDERS, type EmailProvider } from './provider-display';
import {
  IntegrationSyncStatusBadge,
  type IntegrationSyncErrorCode,
} from '@/common/components/IntegrationSyncStatusBadge';

interface EmailAccount {
  id: string;
  email: string;
  provider?: EmailProvider;
  enabledForModule?: boolean;
  /** 'disabled' = Retain-disconnected (data kept, not syncing). */
  status?: 'active' | 'disabled';
  // Sync error state surfaced from the backend (cleared on next successful sync).
  syncError?: string | null;
  syncErrorCode?: IntegrationSyncErrorCode | null;
  syncErrorAt?: string | null;
}

interface CalendarEntry {
  id: string;
  name: string;
  primary: boolean;
  accessRole: string;
  color: string | null;
  selected: boolean;
}

type AccountCalendarsState = Record<string, {
  calendars: CalendarEntry[];
  loading: boolean;
  error: string | null;
}>;

export interface ToggleRow {
  id: string;
  /** Optional left-aligned icon. */
  icon?: React.ReactNode;
  label: string;
  enabled: boolean;
  /** Called with the new value (`!enabled`). */
  onToggle: (next: boolean) => void;
  /** Optional tooltip text. */
  title?: string;
}

interface EmailOptionsProps {
  moduleId: string;
  moduleType?: string;
  conversationId?: string;
  onRename?: () => void;
  onArchive?: () => void;
  onClose?: () => void;
  /**
   * Optional extra toggle rows rendered ABOVE the account list. Each row
   * carries its own toggle handler so this component stays provider-agnostic.
   */
  extraTopRows?: ToggleRow[];
  [legacyProp: string]: unknown;
}

const ACCOUNTS_KEY = 'email-accounts';

export function EmailOptions(props: EmailOptionsProps) {
  const { moduleId, moduleType, onRename, onArchive, onClose, extraTopRows } = props;
  const { token } = useSession();
  const queryClient = useQueryClient();
  const queryKey = [ACCOUNTS_KEY, moduleId] as const;
  const isCalendar = moduleType === 'event_list' || moduleType === 'calendar';
  const useV2Accounts = isV2;

  const { data: accounts = [], isFetched } = useQuery({
    queryKey,
    queryFn: async () => {
      if (useV2Accounts) {
        const res = await api.http.get<{ accounts: Array<{ id: string; email: string; provider: string; tokenValid: boolean; enabledForModule?: boolean; status?: 'active' | 'disabled' }> }>(
          `${emailApiPath()}/status?moduleId=${encodeURIComponent(moduleId)}`,
          { authToken: token! }
        );
        return (res.data.accounts || []).map(a => ({
          id: a.id,
          email: a.email,
          provider: a.provider as EmailProvider,
          enabledForModule: a.enabledForModule ?? a.tokenValid,
          status: a.status ?? 'active',
        }));
      }
      // Legacy V1 (`!isV2`) fallback — Google-only accounts endpoint. Retired
      // wholesale with the repo-wide `isV2` cutover flag, not here.
      const accountsResponse = await api.http.get<{ accounts: EmailAccount[] }>(
        `/integrations/google/accounts?moduleId=${encodeURIComponent(moduleId)}`,
        { authToken: token! }
      );
      return accountsResponse.data.accounts || [];
    },
    enabled: !!token,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Hide the seeded demo (Boris) account when demo data is hidden — it's not a
  // real integration, so showing an enable/disconnect toggle for it is wrong.
  const [hideDemo] = useHideDemo();
  const visibleAccounts = hideDemo
    ? accounts.filter(a => !isDemoIntegrationId(a.id))
    : accounts;

  // Local optimistic state for extraTopRows — flips the switch immediately on click
  // without waiting for the parent to re-render. Mirrors how account toggles
  // use setQueryData rather than threading state through props.
  const [extraRowStates, setExtraRowStates] = useState<Record<string, boolean>>(() =>
    Object.fromEntries((extraTopRows ?? []).map(r => [r.id, r.enabled]))
  );

  // Sync local state when the parent reverts a row's enabled value (e.g., after a
  // mutation error). The dep string encodes just the enabled values so this effect
  // only fires when the actual toggle states change, not on every array-reference churn.
  const extraRowEnabledKey = (extraTopRows ?? []).map(r => `${r.id}:${r.enabled}`).join(',');
  useEffect(() => {
    if (!extraTopRows?.length) return;
    setExtraRowStates(prev => {
      let changed = false;
      const next = { ...prev };
      for (const row of extraTopRows) {
        if (next[row.id] !== undefined && next[row.id] !== row.enabled) {
          next[row.id] = row.enabled;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [extraRowEnabledKey]);

  const [expandedAccountIds, setExpandedAccountIds] = useState<Set<string>>(new Set());
  const [accountCalendars, setAccountCalendars] = useState<AccountCalendarsState>({});

  const fetchCalendarsForAccount = useCallback(async (accountId: string) => {
    if (!token) return;
    setAccountCalendars(prev => ({
      ...prev,
      [accountId]: { calendars: prev[accountId]?.calendars || [], loading: true, error: null },
    }));
    try {
      const response = await api.http.get<{ calendars: CalendarEntry[] }>(
        `/integrations/calendar/${accountId}/calendars?moduleId=${encodeURIComponent(moduleId)}`,
        { authToken: token }
      );
      setAccountCalendars(prev => ({
        ...prev,
        [accountId]: { calendars: sortGoogleCalendars(response.data.calendars), loading: false, error: null },
      }));
    } catch (error) {
      console.error('Failed to fetch calendars:', error);
      setAccountCalendars(prev => ({
        ...prev,
        [accountId]: { calendars: [], loading: false, error: 'Failed to load calendars' },
      }));
    }
  }, [token, moduleId]);

  const handleToggleAccountExpand = useCallback((accountId: string) => {
    setExpandedAccountIds(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
    if (!accountCalendars[accountId]?.calendars?.length && !accountCalendars[accountId]?.loading) {
      fetchCalendarsForAccount(accountId);
    }
  }, [accountCalendars, fetchCalendarsForAccount]);

  const handleToggleCalendar = useCallback(async (
    accountId: string,
    calendarId: string,
    calendarName: string,
    currentlySelected: boolean,
  ) => {
    if (!token) return;
    // Optimistic update
    setAccountCalendars(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        calendars: (prev[accountId]?.calendars || []).map(c =>
          c.id === calendarId ? { ...c, selected: !currentlySelected } : c
        ),
      },
    }));
    try {
      await api.http.put(
        `/integrations/calendar/${accountId}/calendars/${encodeURIComponent(calendarId)}`,
        { selected: !currentlySelected, calendarName, moduleId },
        { authToken: token }
      );
      // Calendar checkbox toggles are module-scoped — only the events module needs to
      // re-render. We deliberately do NOT dispatch `integrationAccountToggled` (which is
      // account-level and triggers email/Drive refetch). Receivers that care about
      // calendar visibility (currently just EventListModule) get the change via the
      // React Query invalidations below.
      //
      // Invalidate every event-typed query so each EventListModule /
      // TimelineModule / CalendarModule refetches. The byType('event') key
      // is the prefix for all module-scoped variants
      // (objectsByTypeQueryKey adds 'mod:moduleId' / 'workspace' / limit /
      // query as suffixes), so a single invalidation covers them all via
      // React Query's prefix matching. We also invalidate the generic
      // objects-list pool because some chat surfaces query events through
      // queryKeys.objects.list() rather than byType.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') }),
        queryClient.invalidateQueries({ queryKey: queryKeys.objects.lists() }),
      ]);
    } catch (error) {
      console.error('Failed to toggle calendar:', error);
      // Revert optimistic update
      setAccountCalendars(prev => ({
        ...prev,
        [accountId]: {
          ...prev[accountId],
          calendars: (prev[accountId]?.calendars || []).map(c =>
            c.id === calendarId ? { ...c, selected: currentlySelected } : c
          ),
        },
      }));
    }
  }, [token, moduleId, queryClient]);

  const handleToggleAccount = useCallback(async (accountId: string, currentlyEnabled: boolean) => {
    if (!token) return;

    // Optimistic update
    queryClient.setQueryData<EmailAccount[]>(queryKey, (prev) =>
      (prev || []).map(a => a.id === accountId ? { ...a, enabledForModule: !currentlyEnabled } : a)
    );

    try {
      if (useV2Accounts) {
        if (currentlyEnabled) {
          await api.http.delete(
            `${emailApiPath()}/${accountId}/modules/${moduleId}/disable`,
            { authToken: token }
          );
        } else {
          await api.http.post(
            `${emailApiPath()}/${accountId}/modules/${moduleId}/enable`,
            {},
            { authToken: token }
          );
        }
      } else {
        if (currentlyEnabled) {
          await api.http.delete(
            `/integrations/google/${accountId}/modules/${moduleId}/disable`,
            { authToken: token }
          );
        } else {
          await api.http.post(
            `/integrations/google/${accountId}/modules/${moduleId}/enable`,
            {},
            { authToken: token }
          );
        }
      }

      // Detail tells EmailPanel whether the account was just enabled (so it
       // can show the full-screen X logo loader for the post-enable sync) vs.
       // just disabled (no full-screen loader needed). `accountId` is included
       // for future surgical-refresh consumers — current listeners may ignore it.
       // `module: 'email'` is the discriminator used by EmailPanel to gate its
       // `sync-now` side effect to email-originated toggles only.
      window.dispatchEvent(new CustomEvent('integrationAccountToggled', {
        detail: { accountId, enabled: !currentlyEnabled, module: 'email' },
      }));
    } catch (error) {
      console.error('Failed to toggle account:', error);
      // Revert optimistic update on failure
      queryClient.setQueryData<EmailAccount[]>(queryKey, (prev) =>
        (prev || []).map(a => a.id === accountId ? { ...a, enabledForModule: currentlyEnabled } : a)
      );
    }
  }, [token, moduleId, queryClient, queryKey]);

  const handleConnectProvider = useCallback(async (provider: 'google' | 'microsoft') => {
    if (provider === 'google') {
      window.dispatchEvent(new Event('triggerGoogleOAuth'));
      onClose?.();
      return;
    }
    if (!token) return;
    // Microsoft: same current-window redirect as Google (not a popup). The
    // provider-oauth callback redirects back to this page on completion.
    try { sessionStorage.setItem('email_oauth_in_progress', 'true'); } catch { /* best-effort */ }
    try {
      // No integrationId → FLOW A (enroll new account): picker shows.
      await connectProvider({ provider: 'microsoft', authToken: token });
    } catch (error) {
      try { sessionStorage.removeItem('email_oauth_in_progress'); } catch { /* best-effort */ }
      console.error('Failed to connect Microsoft account:', error);
    }
  }, [token, onClose]);

  // Nothing to show until the first fetch completes (cached hits are instant)
  if (!isFetched) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
      {extraTopRows && extraTopRows.length > 0 && (
        <>
          {extraTopRows.map((row, idx) => {
            const enabled = extraRowStates[row.id] ?? row.enabled;
            return (
              <div
                key={row.id}
                style={{
                  ...(idx > 0 ? { borderTop: '1px solid var(--color-border)' } : {}),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 18px',
                }}
              >
                <div style={{ flex: 1, minWidth: 0, marginRight: '8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {row.icon}
                  <span
                    style={{
                      fontSize: '14px',
                      color: 'var(--text-primary, var(--color-text))',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={row.title ?? row.label}
                  >
                    {row.label}
                  </span>
                </div>
                <div
                  role="switch"
                  aria-checked={enabled}
                  aria-label={row.label}
                  tabIndex={0}
                  onClick={() => {
                    const next = !enabled;
                    setExtraRowStates(prev => ({ ...prev, [row.id]: next }));
                    row.onToggle(next);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      const next = !enabled;
                      setExtraRowStates(prev => ({ ...prev, [row.id]: next }));
                      row.onToggle(next);
                    }
                  }}
                  style={{
                    position: 'relative',
                    width: '36px',
                    height: '20px',
                    background: enabled ? 'rgba(34, 197, 94, 0.85)' : 'var(--color-surface-hover)',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'background 0.3s ease',
                  }}
                  title={enabled ? 'Click to disable' : 'Click to enable'}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: enabled ? '18px' : '2px',
                      width: '16px',
                      height: '16px',
                      background: 'var(--color-surface)',
                      borderRadius: '50%',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                      transition: 'left 0.3s ease',
                    }}
                  />
                </div>
              </div>
            );
          })}
          {visibleAccounts.length > 0 && <div style={{ borderTop: '1px solid var(--color-border)' }} />}
        </>
      )}
      {visibleAccounts.length === 0 && (
        useV2Accounts ? (
          <>
            {EMAIL_PROVIDERS.map((provider) => {
              const display = getProviderDisplay(provider);
              return (
                <button
                  key={provider}
                  className="context-menu-item"
                  onClick={() => handleConnectProvider(provider)}
                >
                  <img src={display.logo} width="16" height="16" alt="" className="context-menu-item-icon" style={{ objectFit: 'contain' }} />
                  {getConnectLabel(provider)}
                </button>
              );
            })}
          </>
        ) : (
          <button
            className="context-menu-item"
            onClick={() => {
              handleConnectProvider('google');
            }}
          >
            <svg className="context-menu-item-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {getConnectLabel()}
          </button>
        )
      )}
      {visibleAccounts.length > 0 && (
        <>
          {visibleAccounts.map((account, idx) => {
            const isEnabled = !!account.enabledForModule;
            const isDisabled = account.status === 'disabled';
            const isExpanded = expandedAccountIds.has(account.id);
            const calData = accountCalendars[account.id];
            // Only calendar/event-list modules expose the per-calendar picker,
            // and only enabled (not Retain-disconnected) accounts have calendars.
            const canExpand = isCalendar && isEnabled && !isDisabled && account.provider !== 'microsoft';
            return (
              <div key={account.id} style={{ ...(idx > 0 ? { borderTop: '1px solid var(--color-border)' } : {}), overflow: 'hidden' }}>
                <div
                  onClick={canExpand ? () => handleToggleAccountExpand(account.id) : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 18px',
                    cursor: canExpand ? 'pointer' : 'default',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, marginRight: '8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {account.provider && (
                        <img src={getProviderDisplay(account.provider).logo} width="14" height="14" alt="" style={{ flexShrink: 0 }} />
                      )}
                      <span
                        style={{
                          fontSize: '14px',
                          color: isDisabled ? 'var(--color-text-muted)' : 'var(--text-primary, var(--color-text))',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={account.email}
                      >
                        {account.email}
                      </span>
                    </div>
                    {isDisabled && (
                      <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                        Disconnected — data retained
                      </span>
                    )}
                    {account.syncErrorCode && (
                      <IntegrationSyncStatusBadge
                        syncErrorCode={account.syncErrorCode}
                        syncError={account.syncError}
                        syncErrorAt={account.syncErrorAt}
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    {isDisabled ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (account.provider === 'microsoft') {
                            try { sessionStorage.setItem('email_oauth_in_progress', 'true'); } catch { /* best-effort */ }
                            void connectProvider({ provider: 'microsoft', authToken: token })
                              .catch(() => { try { sessionStorage.removeItem('email_oauth_in_progress'); } catch { /* best-effort */ } });
                          } else {
                            window.dispatchEvent(new Event('triggerGoogleOAuth'));
                          }
                        }}
                        style={{
                          background: 'none', border: '1px solid var(--color-border)', borderRadius: '6px',
                          cursor: 'pointer', padding: '4px 10px', fontSize: '12px', fontWeight: 600,
                          color: 'var(--color-primary, #1a9ba5)', whiteSpace: 'nowrap',
                        }}
                        title="Reconnect this account (resumes syncing; retained data stays)"
                      >
                        Reconnect
                      </button>
                    ) : (
                      <>
                    {canExpand && (
                      <svg
                        width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        aria-hidden="true"
                        style={{
                          flexShrink: 0,
                          color: 'var(--color-text-faint)',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                        }}
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    )}
                    <div
                      onClick={(e) => { e.stopPropagation(); handleToggleAccount(account.id, isEnabled); }}
                      style={{
                        position: 'relative',
                        width: '36px',
                        height: '20px',
                        background: isEnabled ? 'rgba(34, 197, 94, 0.85)' : 'var(--color-surface-hover)',
                        borderRadius: '10px',
                        transition: 'background 0.3s ease',
                        cursor: 'pointer',
                      }}
                      title={isEnabled ? 'Click to disable' : 'Click to enable'}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: isEnabled ? '18px' : '2px',
                          width: '16px',
                          height: '16px',
                          background: 'var(--color-surface)',
                          borderRadius: '50%',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                          transition: 'left 0.3s ease',
                        }}
                      />
                    </div>
                      </>
                    )}
                  </div>
                </div>
                {isCalendar && isEnabled && !isDisabled && isExpanded && (
                  <div style={{ padding: '0 18px 8px 18px', backgroundColor: 'var(--color-surface)' }}>
                    {calData?.loading && (
                      <div style={{ fontSize: '11px', color: 'var(--color-text-faint)', padding: '3px 0' }}>Loading calendars...</div>
                    )}
                    {calData?.error && (
                      <div style={{ fontSize: '11px', color: '#ef4444', padding: '3px 0' }}>{calData.error}</div>
                    )}
                    {!calData?.loading && !calData?.error && calData?.calendars.map(cal => {
                      const calColor = cal.color || '#444';
                      return (
                        <div
                          key={cal.id}
                          onClick={(e) => { e.stopPropagation(); handleToggleCalendar(account.id, cal.id, cal.name, cal.selected); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '7px',
                            padding: '3px 0',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: 'var(--color-text-subtle)',
                          }}
                        >
                          <div style={{
                            width: '14px',
                            height: '14px',
                            borderRadius: '2px',
                            border: `2px solid ${calColor}`,
                            backgroundColor: cal.selected ? calColor : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'background-color 0.15s ease',
                          }}>
                            {cal.selected && (
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </div>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {cal.primary ? 'Primary' : cal.name}
                          </span>
                        </div>
                      );
                    })}
                    {!calData?.loading && !calData?.error && (!calData?.calendars || calData.calendars.length === 0) && (
                      <div style={{ fontSize: '11px', color: 'var(--color-text-faint)', padding: '3px 0' }}>No calendars found</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
